import { useState, useEffect } from 'react';
import { getCollection } from '../../services/firestoreService';
import { getUserEngagementInsights } from '../../services/analyticsService';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';

const LearningPreferencesTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<any[]>([]);
  const [engagementInsights, setEngagementInsights] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [prefsData, insights] = await Promise.all([
          getCollection('learningPreferences', undefined, undefined, undefined, 1000).catch(() => []),
          getUserEngagementInsights().catch(() => null),
        ]);
        setPreferences(prefsData);
        setEngagementInsights(insights);
      } catch (error) {
        console.error('Error loading learning preferences:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading learning preferences...</p>
      </div>
    );
  }

  // Aggregate preference data
  const learningStyles: Map<string, number> = new Map();
  const difficultyLevels: Map<string, number> = new Map();
  const categoryPreferences: Map<string, number> = new Map();

  preferences.forEach((pref: any) => {
    if (pref.learningStyle) {
      learningStyles.set(pref.learningStyle, (learningStyles.get(pref.learningStyle) || 0) + 1);
    }
    if (pref.difficultyLevel) {
      difficultyLevels.set(pref.difficultyLevel, (difficultyLevels.get(pref.difficultyLevel) || 0) + 1);
    }
    if (pref.preferredCategories && Array.isArray(pref.preferredCategories)) {
      pref.preferredCategories.forEach((cat: string) => {
        categoryPreferences.set(cat, (categoryPreferences.get(cat) || 0) + 1);
      });
    }
  });

  const learningStylesData = {
    labels: Array.from(learningStyles.keys()),
    datasets: [
      {
        label: 'Users',
        data: Array.from(learningStyles.values()),
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'],
      },
    ],
  };

  const difficultyData = {
    labels: Array.from(difficultyLevels.keys()),
    datasets: [
      {
        label: 'Users',
        data: Array.from(difficultyLevels.values()),
        backgroundColor: ['#ef4444', '#f59e0b', '#10b981', '#2563eb'],
      },
    ],
  };

  const categoryData = {
    labels: Array.from(categoryPreferences.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name]) => name),
    datasets: [
      {
        label: 'Usage',
        data: Array.from(categoryPreferences.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([, count]) => count),
        backgroundColor: '#10b981',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Learning Preferences
      </h1>

      {/* KPI Card */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Users with Preferences</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {preferences.length}
          </h2>
        </div>
        {engagementInsights && (
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Personalized Categories</p>
            <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
              {engagementInsights.popularCategories?.length || 0}
            </h2>
          </div>
        )}
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        {learningStyles.size > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <h3 style={{ marginTop: 0 }}>Learning Style Distribution</h3>
            <PieChart data={learningStylesData} height={300} />
          </div>
        )}

        {difficultyLevels.size > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <h3 style={{ marginTop: 0 }}>Difficulty Level Preference</h3>
            <BarChart data={difficultyData} height={300} />
          </div>
        )}

        {categoryPreferences.size > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}>
            <h3 style={{ marginTop: 0 }}>Top Category Preferences</h3>
            <BarChart data={categoryData} height={300} />
          </div>
        )}
      </div>

      {/* Personalized Categories from Analytics */}
      {engagementInsights && engagementInsights.popularCategories && engagementInsights.popularCategories.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Popular Personalized Categories (Last 30 Days)
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Usage Count</th>
              </tr>
            </thead>
            <tbody>
              {engagementInsights.popularCategories.slice(0, 20).map((cat: any, index: number) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem' }}>{cat.name}</td>
                  <td style={{ padding: '1rem' }}>{cat.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LearningPreferencesTab;

